import {
  normalizeTimeOfDay,
  serializeTimeOfDay,
  type FilterMappingConfig,
} from "@/utils/filterNormalization";

const runsMapping = {
  search: {
    queryKey: "q.search",
    type: "string",
    persisted: true,
  },
  companies: {
    queryKey: "q.companies",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  vehicles: {
    queryKey: "q.vehicles",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  run_groups: {
    queryKey: "q.run_groups",
    type: "multi",
    itemType: "string",
    persisted: false,
  },
  service_areas: {
    queryKey: "q.service_areas",
    type: "multi",
    itemType: "string",
    persisted: false,
  },
  allowed_route_type: {
    queryKey: "q.allowed_route_type",
    type: "enum",
    persisted: false,
    parse: (value: string) => {
      if (value === "0" || value === "demand") return "demand";
      if (value === "1" || value === "fixed_route") return "fixed_route";
      return undefined;
    },
    serialize: (value: unknown) => {
      if (value === "demand") return "0";
      if (value === "fixed_route") return "1";
      return undefined;
    },
  },
  availability: {
    queryKey: "q.availability",
    type: "boolean",
    persisted: true,
  },
  delivery_system: {
    queryKey: "q.delivery_system",
    type: "number",
    persisted: true,
  },
  enabled: {
    queryKey: "q.enabled",
    type: "boolean",
    persisted: true,
  },
  home_city: {
    queryKey: "q.home_city",
    type: "string",
    persisted: false,
  },
  home_street: {
    queryKey: "q.home_street",
    type: "string",
    persisted: false,
  },
  home_location: {
    queryKey: "q.home_location",
    type: "multi",
    itemType: "string",
    persisted: false,
    parse: (value: string) =>
      value
        .split(/[~,]/)
        .map((v) => v.trim())
        .filter(Boolean),
    serialize: (value: unknown) => {
      const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      return values.length > 0 ? values.join("~") : undefined;
    },
  },
} as const satisfies FilterMappingConfig;

const vehiclesMapping = {
  search: { queryKey: "q.search", type: "string", persisted: true },
  enabled: { queryKey: "q.enabled", type: "boolean", persisted: true },
  vehicle_type: { queryKey: "q.vehicle_type", type: "number", persisted: true },
  companies: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  mdt_connected: { queryKey: "q.mdt_connected", type: "boolean", persisted: true },
  run_exist: { queryKey: "q.run_exist", type: "boolean", persisted: true },
  run_groups: { queryKey: "q.run_groups", type: "multi", itemType: "string", persisted: false },
  delivery_system: { queryKey: "q.delivery_system", type: "number", persisted: true },
} as const satisfies FilterMappingConfig;

const driversMapping = {
  companies: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  terminated: {
    queryKey: "q.terminated",
    type: "enum",
    persisted: true,
    parse: (value: string) => (value === "1" || value === "0" ? value : undefined),
    serialize: (value: unknown) => (value === "1" || value === "0" ? String(value) : undefined),
  },
  assigned: {
    queryKey: "q.assigned",
    type: "enum",
    persisted: true,
    parse: (value: string) => (value === "1" || value === "0" ? value : undefined),
    serialize: (value: unknown) => (value === "1" || value === "0" ? String(value) : undefined),
  },
} as const satisfies FilterMappingConfig;

const tripsMapping = {
  status: { queryKey: "q.status", type: "multi", itemType: "string", persisted: true },
  companies: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  cancel_types: { queryKey: "q.cancel_types", type: "multi", itemType: "string", persisted: true },
  trip_id: { queryKey: "q.trip_id", type: "string", persisted: true },
  trip_date_from: { queryKey: "q.trip_date_from", type: "string", persisted: true },
  trip_date_to: { queryKey: "q.trip_date_to", type: "string", persisted: true },
  no_run: { queryKey: "q.no_run", type: "boolean", persisted: true },
  target_runs: { queryKey: "q.target_runs", type: "multi", itemType: "string", persisted: true },
  fare_types: { queryKey: "q.fare_types", type: "multi", itemType: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const fundingSourcesMapping = {
  type: { queryKey: "q.type", type: "multi", itemType: "string", persisted: true },
  sponsors: { queryKey: "q.sponsors", type: "multi", itemType: "string", persisted: true },
  purposes: { queryKey: "q.purposes", type: "multi", itemType: "string", persisted: true },
  billing_code_sets: {
    queryKey: "q.billing_code_sets",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  programs: { queryKey: "q.programs", type: "multi", itemType: "string", persisted: true },
  default_provider_exist: {
    queryKey: "q.default_provider_exist",
    type: "boolean",
    persisted: true,
  },
  companies: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  service_areas: {
    queryKey: "q.service_areas",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  available_for_selfservice: {
    queryKey: "q.available_for_selfservice",
    type: "boolean",
    persisted: true,
  },
} as const satisfies FilterMappingConfig;

const providersMapping = {
  search: { queryKey: "q.search", type: "string", persisted: true },
  provider_type: { queryKey: "q.provider_type", type: "enum", persisted: true },
  service_areas: {
    queryKey: "q.service_areas",
    type: "multi",
    itemType: "number",
    persisted: true,
  },
} as const satisfies FilterMappingConfig;

const billingCodesMapping = {
  ui_type: {
    queryKey: "q.ui_type",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      switch (value) {
        case "0":
          return "Flat fee";
        case "1":
          return "Age";
        case "2":
          return "Distance";
        case "3":
          return "Duration";
        case "4":
          return "Zone";
        case "5":
          return "Flat distance";
        case "6":
          return "Flat duration";
        default:
          return undefined;
      }
    },
    serialize: (value: unknown) => {
      switch (value) {
        case "Flat fee":
          return "0";
        case "Age":
          return "1";
        case "Distance":
          return "2";
        case "Duration":
          return "3";
        case "Zone":
          return "4";
        case "Flat distance":
          return "5";
        case "Flat duration":
          return "6";
        default:
          return undefined;
      }
    },
  },
} as const satisfies FilterMappingConfig;

const usersMapping = {
  status: { queryKey: "q.status", type: "string", persisted: true },
  subcontractor: { queryKey: "q.subcontractor", type: "string", persisted: true },
  roles: { queryKey: "q.roles", type: "multi", itemType: "string", persisted: true },
  companies: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  majors: { queryKey: "q.majors", type: "multi", itemType: "string", persisted: true },
  search: { queryKey: "q.search", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const schedulesMapping = {
  // General section
  timePeriod: {
    queryKey: "q.range_default_enum",
    type: "enum",
    persisted: true,
    // Empty string ("No filter") must round-trip through the URL so that explicitly
    // selecting "No filter" is preserved after Apply rather than falling back to the
    // initialDefaultsOnFirstLoad default ("today"). We use the sentinel "no_filter"
    // in the URL, matching the same pattern used by riderPolicyViolationsMapping.timePeriod.
    //
    // The UI (SchedulesFilterPanel timePeriodOptions + SchedulesPage getDateRange) uses
    // camelCase internal values ("previousWeek", "currentWeek", "nextWeek", "fromToday",
    // "dateRange"). The URL / DRT layer uses snake_case ("previous_week", "current_week",
    // "next_week", "from_today", "date_range"). These functions translate between the two.
    parse: (value: string) => {
      if (value === "no_filter") return "";
      const normalized = value.trim().toLowerCase();
      if (normalized === "previous_week" || normalized === "previousweek") return "previousWeek";
      if (normalized === "current_week" || normalized === "currentweek") return "currentWeek";
      if (normalized === "next_week" || normalized === "nextweek") return "nextWeek";
      if (normalized === "from_today" || normalized === "fromtoday") return "fromToday";
      if (normalized === "date_range" || normalized === "daterange") return "dateRange";
      // Pass through well-known lowercase values and any unknown values as-is.
      return value || undefined;
    },
    serialize: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return "no_filter";
      if (value === "previousWeek") return "previous_week";
      if (value === "currentWeek") return "current_week";
      if (value === "nextWeek") return "next_week";
      if (value === "fromToday") return "from_today";
      if (value === "dateRange") return "date_range";
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  date: { queryKey: "q.date", type: "string", persisted: true },
  fromDate: { queryKey: "q.from_date", type: "string", persisted: true },
  toDate: { queryKey: "q.to_date", type: "string", persisted: true },
  fromTime: {
    queryKey: "q.from_time",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  toTime: {
    queryKey: "q.to_time",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  // Empty string ("No filter") must round-trip through the URL so that explicitly
  // selecting "No filter" is preserved after Apply rather than falling back to the
  // initialDefaultsOnFirstLoad default ("not_completed"). We use the sentinel "no_filter"
  // in the URL, matching the same pattern used by schedulesMapping.timePeriod.
  status: {
    queryKey: "q.status",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "no_filter") return "";
      if (value === "not_complete" || value === "not_completed") return "not_completed";
      return value || undefined;
    },
    serialize: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return "no_filter";
      if (value === "not_completed" || value === "not_complete") return "not_completed";
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  // Empty string ("No filter") must round-trip through the URL so that explicitly
  // selecting "No filter" is preserved after Apply rather than falling back to the
  // initialDefaultsOnFirstLoad default ("after"). We use the sentinel "no_filter"
  // in the URL, matching the same pattern used by schedulesMapping.timePeriod.
  showStops: {
    queryKey: "q.show_stops",
    type: "enum",
    persisted: false,
    parse: (value: string) => {
      if (value === "no_filter") return "";
      if (value === "range" || value === "timeRange") return "timeRange";
      if (value === "current" || value === "recent" || value === "currentPlusMinusOneHour")
        return "currentPlusMinusOneHour";
      // "after" and "until_end_of_day" both map to "untilEndOfDay" (the UI value)
      if (value === "after" || value === "until_end_of_day" || value === "untilEndOfDay")
        return "untilEndOfDay";
      if (value === "before") return "before";
      if (value === "minus_240" || value === "minus240") return "minus240";
      if (value === "minus_60" || value === "minus60") return "minus60";
      if (value === "minus_15" || value === "minus15") return "minus15";
      if (value === "plus_15" || value === "plus15") return "plus15";
      if (value === "plus_60" || value === "plus60") return "plus60";
      if (value === "plus_240" || value === "plus240") return "plus240";
      return value || undefined;
    },
    serialize: (value: unknown) => {
      switch (value) {
        case "timeRange":
          return "range";
        case "currentPlusMinusOneHour":
          return "current";
        case "after":
        case "untilEndOfDay":
          return "until_end_of_day";
        case "before":
          return "before";
        case "minus240":
          return "minus_240";
        case "minus60":
          return "minus_60";
        case "minus15":
          return "minus_15";
        case "plus15":
          return "plus_15";
        case "plus60":
          return "plus_60";
        case "plus240":
          return "plus_240";
        default:
          // Empty string ("No filter") serializes to sentinel "no_filter" so it
          // round-trips through the URL instead of being dropped and reverting
          // to the initialDefaultsOnFirstLoad default ("untilEndOfDay").
          if (value === "" || value === null || value === undefined) return "no_filter";
          return typeof value === "string" && value.trim() !== "" ? value : undefined;
      }
    },
  },
  stopsFrom: { queryKey: "q.stops_from", type: "string", persisted: false },
  stopsTo: { queryKey: "q.stops_to", type: "string", persisted: false },

  // Scheduling section
  hasRun: { queryKey: "q.has_run", type: "enum", persisted: true },
  hasTripGroup: { queryKey: "q.has_trip_group", type: "enum", persisted: true },
  provider: { queryKey: "q.provider", type: "multi", itemType: "string", persisted: true },
  providerType: { queryKey: "q.provider_type", type: "multi", itemType: "string", persisted: true },
  run: { queryKey: "q.run", type: "multi", itemType: "string", persisted: true },
  vehicle: { queryKey: "q.vehicle", type: "multi", itemType: "string", persisted: true },
  fleet: { queryKey: "q.fleet", type: "multi", itemType: "string", persisted: true },
  tripGroup: { queryKey: "q.trip_group", type: "multi", itemType: "string", persisted: true },
  device: { queryKey: "q.device", type: "multi", itemType: "string", persisted: true },
  vehicleRequirement: {
    queryKey: "q.vehicle_requirement",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  mobility: { queryKey: "q.mobility", type: "enum", persisted: true },

  // Location section
  pickupCity: { queryKey: "q.pickup_city", type: "string", persisted: true },
  dropoffCity: { queryKey: "q.dropoff_city", type: "string", persisted: true },
  street: { queryKey: "q.street", type: "string", persisted: true },
  pickupTimeZone: { queryKey: "q.pickup_time_zone", type: "string", persisted: true },
  dropoffTimeZone: { queryKey: "q.dropoff_time_zone", type: "string", persisted: true },
  streetSegment: { queryKey: "q.street_segment", type: "string", persisted: false },
  location: {
    queryKey: "q.location",
    type: "multi",
    itemType: "string",
    persisted: false,
    parse: (value: string) =>
      value
        .split(/[~,]/)
        .map((v) => v.trim())
        .filter(Boolean),
    serialize: (value: unknown) => {
      const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      return values.length > 0 ? values.join("~") : undefined;
    },
  },

  // Funding section
  fundingSource: {
    queryKey: "q.funding_source",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  sponsor: { queryKey: "q.sponsor", type: "multi", itemType: "string", persisted: true },
  purpose: { queryKey: "q.purpose", type: "multi", itemType: "string", persisted: true },
  billingCodeSet: {
    queryKey: "q.billing_code_set",
    type: "multi",
    itemType: "string",
    persisted: true,
  },

  // Client section
  client: { queryKey: "q.client", type: "string", persisted: true },
  clientNumber: { queryKey: "q.client_number", type: "string", persisted: true },
  clientCounty: { queryKey: "q.client_county", type: "multi", itemType: "string", persisted: true },

  // Other section
  schedule: { queryKey: "q.schedule", type: "string", persisted: true },
  trip: { queryKey: "q.trip", type: "string", persisted: true },
  externalTripId: { queryKey: "q.external_trip_id", type: "string", persisted: true },
  subscription: { queryKey: "q.subscription", type: "string", persisted: true },
  fixedRouteType: {
    queryKey: "q.fixed_route_type",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  fixedRoute: { queryKey: "q.fixed_route", type: "multi", itemType: "string", persisted: true },
  review: { queryKey: "q.review", type: "enum", persisted: true },
  dispatchType: { queryKey: "q.dispatch_type", type: "enum", persisted: true },
  subcontractor: { queryKey: "q.subcontractor", type: "enum", persisted: true },
} as const satisfies FilterMappingConfig;

const subscriptionsMapping = {
  // General filters
  dayOfWeek: {
    queryKey: "q.weekdays",
    type: "multi",
    itemType: "string",
    persisted: true,
    parse: (value: string) => {
      const dayMap: Record<string, string> = {
        "0": "Monday",
        "1": "Tuesday",
        "2": "Wednesday",
        "3": "Thursday",
        "4": "Friday",
        "5": "Saturday",
        "6": "Sunday",
        Monday: "Monday",
        Tuesday: "Tuesday",
        Wednesday: "Wednesday",
        Thursday: "Thursday",
        Friday: "Friday",
        Saturday: "Saturday",
        Sunday: "Sunday",
      };

      return value
        .split(/[~,]/)
        .map((entry) => dayMap[entry.trim()])
        .filter((entry): entry is string => Boolean(entry));
    },
    serialize: (value: unknown) => {
      const dayMap: Record<string, string> = {
        Monday: "0",
        Tuesday: "1",
        Wednesday: "2",
        Thursday: "3",
        Friday: "4",
        Saturday: "5",
        Sunday: "6",
      };

      const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      const serializedValues = values
        .map((entry) => dayMap[String(entry)])
        .filter((entry): entry is string => Boolean(entry));

      return serializedValues.length > 0 ? serializedValues.join("~") : undefined;
    },
  },
  status: {
    queryKey: "q.status",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "no_filter") return "";
      if (value === "no_cancel" || value === "not_cancelled") return "not_cancelled";
      return value;
    },
    serialize: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return "no_filter";
      if (value === "not_cancelled") return "no_cancel";
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  tripType: {
    queryKey: "q.trip_type",
    type: "enum",
    persisted: true,
    parse: (value: string) => (value ? [value] : []),
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      return typeof firstValue === "string" && firstValue.trim() !== "" ? firstValue : undefined;
    },
  },
  willCall: {
    queryKey: "q.will_call_status",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "1";
      if (firstValue === "no") return "0";
      return undefined;
    },
  },
  preVerificationStatus: {
    queryKey: "q.trip_verification_statuses",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  timePeriod: {
    queryKey: "q.range_default_enum",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "fromToday") return "from_today";
      if (value === "dateRange") return "date_range";
      return value;
    },
    serialize: (value: unknown) => {
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  fromDate: { queryKey: "q.range_default_date_from", type: "string", persisted: true },
  toDate: { queryKey: "q.range_default_date_to", type: "string", persisted: true },
  fromTime: {
    queryKey: "q.range_default_time_from",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  toTime: {
    queryKey: "q.range_default_time_to",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },

  // Scheduling section
  hasProvider: {
    queryKey: "q.company_exist",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "1";
      if (firstValue === "no") return "0";
      return undefined;
    },
  },
  hasTripGroup: {
    queryKey: "q.trip_group_exist",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "1";
      if (firstValue === "no") return "0";
      return undefined;
    },
  },
  privateTrip: {
    queryKey: "q.travel_alone",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "true";
      if (firstValue === "no") return "false";
      return undefined;
    },
  },
  assignedProvider: { queryKey: "q.target_companies", type: "multi", itemType: "string", persisted: true },
  targetRun: { queryKey: "q.target_runs", type: "multi", itemType: "string", persisted: true },
  targetRunUse: {
    queryKey: "q.target_run_type",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "exclude" || value === "no") return ["no"];
      if (value === "only" || value === "yes") return ["yes"];
      if (value === "preferred" || value === "mandatory") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "only";
      if (firstValue === "no") return "exclude";
      if (firstValue === "preferred" || firstValue === "mandatory") return String(firstValue);
      return undefined;
    },
  },
  tripGroup: { queryKey: "q.trip_groups", type: "multi", itemType: "string", persisted: true },
  device: { queryKey: "q.devices", type: "multi", itemType: "string", persisted: true },
  vehicleRequirement: {
    queryKey: "q.vehicle_requirements",
    type: "multi",
    itemType: "string",
    persisted: true,
    parse: (value: string) => {
      const reverseVehicleCodeMap: Record<string, string> = {
        inva: "accessible_bus",
        veh: "passenger_car",
        van: "van",
        veh_van: "passenger_car_or_van",
        inva_van: "accessible_bus_or_van",
        inva_veh: "accessible_bus_or_passenger_car",
      };

      return value
        .split(/[~,]/)
        .map((entry) => reverseVehicleCodeMap[entry.trim()] || entry.trim())
        .filter(Boolean);
    },
    serialize: (value: unknown) => {
      const vehicleCodeMap: Record<string, string> = {
        accessible_bus: "inva",
        passenger_car: "veh",
        van: "van",
        passenger_car_or_van: "veh_van",
        accessible_bus_or_van: "inva_van",
        accessible_bus_or_passenger_car: "inva_veh",
      };

      const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      const serializedValues = values
        .map((entry) => vehicleCodeMap[String(entry)] || String(entry))
        .filter(Boolean);

      return serializedValues.length > 0 ? serializedValues.join("~") : undefined;
    },
  },
  mobility: {
    queryKey: "q.mobility",
    type: "enum",
    persisted: true,
    parse: (value: string) => (value ? [value] : []),
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      return typeof firstValue === "string" && firstValue.trim() !== "" ? firstValue : undefined;
    },
  },

  // Location section
  start_city: { queryKey: "q.start_city", type: "string", persisted: true },
  end_city: { queryKey: "q.end_city", type: "string", persisted: true },
  street: { queryKey: "q.street", type: "string", persisted: true },
  ideal_length_from: { queryKey: "q.ideal_length_from", type: "string", persisted: true },
  ideal_length_to: { queryKey: "q.ideal_length_to", type: "string", persisted: true },
  idealOnboardTimeFrom: { queryKey: "q.ideal_duration_from", type: "string", persisted: true },
  idealOnboardTimeTo: { queryKey: "q.ideal_duration_to", type: "string", persisted: true },

  // Funding section
  fundingSource: {
    queryKey: "q.majors",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  sponsor: { queryKey: "q.sponsors", type: "multi", itemType: "string", persisted: true },
  purpose: { queryKey: "q.purposes", type: "multi", itemType: "string", persisted: true },
  billingCodeSet: {
    queryKey: "q.billing_code_sets",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  fareType: { queryKey: "q.fare_types", type: "multi", itemType: "string", persisted: true },

  // Client section
  clientName: { queryKey: "q.customer_name", type: "string", persisted: true },
  clientNumber: { queryKey: "q.customer_number", type: "string", persisted: true },
  clientCounty: { queryKey: "q.customer_counties", type: "multi", itemType: "string", persisted: true },

  // Origin
  viaImport: {
    queryKey: "q.origin_import",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "1";
      if (firstValue === "no") return "0";
      return undefined;
    },
  },

  // Other section
  renegotiated: {
    queryKey: "q.renegotiated",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (["1", "true", "yes"].includes(value)) return ["yes"];
      if (["0", "false", "no"].includes(value)) return ["no"];
      if (value === "yes" || value === "no") return [value];
      return [];
    },
    serialize: (value: unknown) => {
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue === "yes") return "1";
      if (firstValue === "no") return "0";
      return undefined;
    },
  },
  locationId: {
    queryKey: "q.locationId",
    type: "multi",
    itemType: "string",
    persisted: false,
    parse: (value: string) =>
      value
        .split(/[~,]/)
        .map((v) => v.trim())
        .filter(Boolean),
    serialize: (value: unknown) => {
      const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      return values.length > 0 ? values.join("~") : undefined;
    },
  },
  scheduleId: { queryKey: "q.route_id", type: "string", persisted: true },
  tripId: { queryKey: "q.trip_id", type: "string", persisted: true },
  subscriptionId: { queryKey: "q.subscription_id", type: "string", persisted: true },
  subscriptionFromTime: {
    queryKey: "q.subscription_time_from",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  subscriptionToTime: {
    queryKey: "q.subscription_time_to",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
} as const satisfies FilterMappingConfig;

const roadCallsMapping = {
  timePeriod: {
    queryKey: "q.range_default_enum",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "time") return "date";
      return value;
    },
    serialize: (value: unknown) => {
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  fromDate: { queryKey: "q.range_default_date_from", type: "string", persisted: true },
  toDate: { queryKey: "q.range_default_date_to", type: "string", persisted: true },
  singleDate: { queryKey: "q.range_default_date", type: "string", persisted: true },
  updatedAfter: { queryKey: "q.modified_after", type: "string", persisted: true },
  category: { queryKey: "q.categories", type: "multi", itemType: "string", persisted: true },
  provider: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  vehicle: { queryKey: "q.vehicles", type: "multi", itemType: "string", persisted: true },
  driver: { queryKey: "q.drivers", type: "multi", itemType: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const expensesMapping = {
  timePeriod: { queryKey: "q.range_default_enum", type: "enum", persisted: true },
  startDate: { queryKey: "q.range_default_date_from", type: "string", persisted: true },
  endDate: { queryKey: "q.range_default_date_to", type: "string", persisted: true },
  expenseType: {
    queryKey: "q.expense_category",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "DISTANCE_EXPENSE" || value === "distance" || value === "1") return "distance";
      if (value === "ADDITIONAL_EXPENSE" || value === "additional") return "additional";
      return undefined;
    },
    serialize: (value: unknown) => {
      if (value === "distance") return "DISTANCE_EXPENSE";
      if (value === "additional") return "ADDITIONAL_EXPENSE";
      return undefined;
    },
  },
  category: { queryKey: "q.expense_type", type: "multi", itemType: "string", persisted: true },
  status: {
    queryKey: "q.status",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      const normalized = value.trim().toLowerCase().replace(/[_ ]/g, " ");
      if (normalized === "review required") return "review";
      if (normalized === "approved") return "approved";
      if (normalized === "denied") return "denied";
      return undefined;
    },
    serialize: (value: unknown) => {
      if (value === "review") return "Review required";
      if (value === "approved") return "Approved";
      if (value === "denied") return "Denied";
      return undefined;
    },
  },
  name: { queryKey: "q.name", type: "string", persisted: true },
  client: { queryKey: "q.customers", type: "multi", itemType: "string", persisted: true },
  reimbursement: { queryKey: "q.reimbursement_id", type: "string", persisted: true },
  verificationReasons: {
    queryKey: "q.verification_reasons",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  denialReason: {
    queryKey: "q.expense_denial_reason",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  fundingSource: {
    queryKey: "q.major_funding_sources",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  purpose: { queryKey: "q.purposes", type: "multi", itemType: "string", persisted: true },
  provider: { queryKey: "q.companies", type: "multi", itemType: "string", persisted: true },
  expense: { queryKey: "q.expense_id", type: "string", persisted: true },
  originalExpense: { queryKey: "q.original_expense_id", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const reimbursementsMapping = {
  timePeriod: {
    queryKey: "q.range_default_enum",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      const normalized = value.trim().toLowerCase();
      if (normalized === "date" || normalized === "daterange") return "dateRange";
      if (normalized === "time") return "date";
      if (normalized === "previous_week" || normalized === "previousweek") return "previousWeek";
      if (normalized === "current_week" || normalized === "currentweek") return "currentWeek";
      if (normalized === "next_week" || normalized === "nextweek") return "nextWeek";
      if (normalized === "from_today" || normalized === "fromtoday") return "fromToday";
      if (normalized === "today") return "today";
      if (normalized === "yesterday") return "yesterday";
      if (normalized === "tomorrow") return "tomorrow";
      return undefined;
    },
    serialize: (value: unknown) => {
      if (value === "dateRange") return "date";
      if (value === "date") return "time";
      if (value === "previousWeek") return "previous_week";
      if (value === "currentWeek") return "current_week";
      if (value === "nextWeek") return "next_week";
      if (value === "fromToday") return "from_today";
      if (value === "today") return "today";
      if (value === "yesterday") return "yesterday";
      if (value === "tomorrow") return "tomorrow";
      return undefined;
    },
  },
  fromDate: { queryKey: "q.range_default_date_from", type: "string", persisted: true },
  toDate: { queryKey: "q.range_default_date_to", type: "string", persisted: true },
  specificDate: { queryKey: "q.range_default_date", type: "string", persisted: true },
  fromTime: {
    queryKey: "q.range_default_time_from",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  toTime: {
    queryKey: "q.range_default_time_to",
    type: "string",
    persisted: true,
    parse: (value: string) => normalizeTimeOfDay(value),
    serialize: (value: unknown) => serializeTimeOfDay(value),
  },
  medicalFacility: {
    queryKey: "q.healthcare_provider_name",
    type: "string",
    persisted: true,
  },
  client: { queryKey: "q.customer", type: "multi", itemType: "string", persisted: true },
  fundingSource: {
    queryKey: "q.funding_source",
    type: "multi",
    itemType: "string",
    persisted: true,
  },
  purpose: { queryKey: "q.purpose", type: "multi", itemType: "string", persisted: true },
  requestStatus: {
    queryKey: "q.status",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      const normalized = value.trim().toLowerCase().replace(/[_ ]/g, " ");
      if (normalized === "new") return "NEW";
      if (normalized === "in review") return "IN_REVIEW";
      if (normalized === "approved") return "APPROVED";
      if (normalized === "denied") return "DENIED";
      if (normalized === "disbursed") return "DISBURSED";
      if (normalized === "canceled" || normalized === "cancelled") return "CANCELED";
      return undefined;
    },
    serialize: (value: unknown) => {
      if (value === "NEW") return "New";
      if (value === "IN_REVIEW") return "In review";
      if (value === "APPROVED") return "Approved";
      if (value === "DENIED") return "Denied";
      if (value === "DISBURSED") return "Disbursed";
      if (value === "CANCELED") return "Canceled";
      return undefined;
    },
  },
  parentReimbursement: {
    queryKey: "q.parent_reimbursement",
    type: "string",
    persisted: true,
  },
  reimbursement: { queryKey: "q.reimbursement_id", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const batchJobsMapping = {
  type: { queryKey: "q.type", type: "string", persisted: true },
  enabled: {
    queryKey: "q.enabled",
    type: "enum",
    persisted: true,
    parse: (value: string) => {
      if (value === "no_filter") return "";
      return value === "1" || value === "0" ? value : undefined;
    },
    serialize: (value: unknown) => {
      if (value === "") return "no_filter";
      return value === "1" || value === "0" ? String(value) : undefined;
    },
  },
  run_asap: { queryKey: "q.run_asap", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const riderPolicyViolationsMapping = {
  timePeriod: {
    queryKey: "q.range_default_enum",
    type: "enum",
    persisted: true,
    // Empty string ("No filter") must round-trip through the URL so that explicitly
    // selecting "No filter" is preserved after Apply rather than falling back to the
    // default ("today"). We use the sentinel "no_filter" in the URL, matching the
    // same pattern used by batchJobsMapping.enabled.
    parse: (value: string) => {
      if (value === "no_filter") return "";
      return value || undefined;
    },
    serialize: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return "no_filter";
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  fromDate: { queryKey: "q.range_default_date_from", type: "string", persisted: true },
  toDate: { queryKey: "q.range_default_date_to", type: "string", persisted: true },
  singleDate: { queryKey: "q.range_default_date", type: "string", persisted: true },
  customerNumber: { queryKey: "q.customer_number", type: "string", persisted: true },
  printed: { queryKey: "q.printed", type: "string", persisted: true },
  active: { queryKey: "q.active", type: "string", persisted: true },
  suspensionStatus: {
    queryKey: "q.suspension_status",
    type: "enum",
    persisted: true,
    // Empty string ("No filter") must round-trip through the URL so that explicitly
    // selecting "No filter" is preserved after Apply rather than falling back to the
    // default ("ALL_VALID"). We use the sentinel "no_filter" in the URL.
    parse: (value: string) => {
      if (value === "no_filter") return "";
      return value || undefined;
    },
    serialize: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return "no_filter";
      return typeof value === "string" && value.trim() !== "" ? value : undefined;
    },
  },
  cancelTypes: { queryKey: "q.cancel_types", type: "multi", itemType: "string", persisted: true },
  majors: { queryKey: "q.majors", type: "multi", itemType: "string", persisted: true },
  majorSet: { queryKey: "q.major_set", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

const riderPolicyPotentialViolationsMapping = {
  endDate: { queryKey: "q.end_date", type: "string", persisted: true },
  suspensionStatus: { queryKey: "q.suspension_status", type: "string", persisted: true },
  cancelTypes: { queryKey: "q.cancel_types", type: "multi", itemType: "string", persisted: true },
  majors: { queryKey: "q.majors", type: "multi", itemType: "string", persisted: true },
  majorSet: { queryKey: "q.major_set", type: "string", persisted: true },
} as const satisfies FilterMappingConfig;

// ── Clients (Customers) ──────────────────────────────────────────────
// Mirror of CLIENT_FILTER_KEY_MAP in useClients.ts, expressed in the
// normalised FilterMappingConfig format for QueryBuilder parity.
const clientsMapping = {
  // General
  customer_name:       { queryKey: "q.customer_name",       type: "string",  persisted: true  },
  customer_number:     { queryKey: "q.customer_number",     type: "string",  persisted: true  },
  medicaid_number:     { queryKey: "q.medicaid_number",     type: "string",  persisted: true  },
  date_of_birth_from:  { queryKey: "q.date_of_birth_from",  type: "string",    persisted: true  },
  date_of_birth_to:    { queryKey: "q.date_of_birth_to",    type: "string",    persisted: true  },
  phone:               { queryKey: "q.phone",               type: "string",  persisted: true  },
  status:              { queryKey: "q.status",              type: "string",  persisted: true  },
  selfservice_status:  { queryKey: "q.selfservice_status",  type: "string",  persisted: false },

  // Mobility / Devices
  default_devices:     { queryKey: "q.default_devices",     type: "multi", itemType: "string", persisted: true },
  vehicle_requirements:{ queryKey: "q.vehicle_requirements", type: "multi", itemType: "string", persisted: true },

  // Location
  home_city:           { queryKey: "q.home_city",           type: "string",  persisted: false },
  home_street:         { queryKey: "q.home_street",         type: "string",  persisted: false },
  home_timezone:       { queryKey: "q.home_timezone",       type: "string",  persisted: false },
  home_counties:       { queryKey: "q.home_counties",       type: "multi", itemType: "string", persisted: false },

  // Funding
  majors:              { queryKey: "q.majors",              type: "multi", itemType: "string", persisted: true },
  sponsors:            { queryKey: "q.sponsors",            type: "multi", itemType: "string", persisted: true },
  purposes:            { queryKey: "q.purposes",            type: "multi", itemType: "string", persisted: true },
  fare_types:          { queryKey: "q.fare_types",          type: "multi", itemType: "string", persisted: true },
  certification_date_from: { queryKey: "q.certification_date_from", type: "string", persisted: false },
  certification_date_to:   { queryKey: "q.certification_date_to",   type: "string", persisted: false },
  eligibility_end_from:    { queryKey: "q.eligibility_end_from",    type: "string", persisted: false },
  eligibility_end_to:      { queryKey: "q.eligibility_end_to",      type: "string", persisted: false },
  balance_from:        { queryKey: "q.balance_from",        type: "number",  persisted: false },
  balance_to:          { queryKey: "q.balance_to",          type: "number",  persisted: false },
  epm_exist:           { queryKey: "q.epm_exist",           type: "string",  persisted: false },

  // Other
  last_ride_timestamp_from: { queryKey: "q.last_ride_timestamp_from", type: "string", persisted: false },
  last_ride_timestamp_to:   { queryKey: "q.last_ride_timestamp_to",   type: "string", persisted: false },
  creation_ts_from:    { queryKey: "q.creation_ts_from",    type: "string",    persisted: false },
  creation_ts_to:      { queryKey: "q.creation_ts_to",      type: "string",    persisted: false },
  default_provider_exist:  { queryKey: "q.default_provider_exist",  type: "string", persisted: false },
  companies:           { queryKey: "q.companies",           type: "multi", itemType: "string", persisted: true },
  trip_groups:         { queryKey: "q.trip_groups",         type: "multi", itemType: "string", persisted: false },
  extra:               { queryKey: "q.extra",               type: "string",  persisted: false },
  schedule:            { queryKey: "q.schedule",            type: "string",  persisted: false },
} as const satisfies FilterMappingConfig;

export const FILTER_MAPPINGS = {
  runs: runsMapping,
  vehicles: vehiclesMapping,
  drivers: driversMapping,
  trips: tripsMapping,
  fundingSources: fundingSourcesMapping,
  billingCodes: billingCodesMapping,
  providers: providersMapping,
  users: usersMapping,
  schedules: schedulesMapping,
  subscriptions: subscriptionsMapping,
  roadCalls: roadCallsMapping,
  expenses: expensesMapping,
  reimbursements: reimbursementsMapping,
  batchJobs: batchJobsMapping,
  riderPolicyViolations: riderPolicyViolationsMapping,
  riderPolicyPotentialViolations: riderPolicyPotentialViolationsMapping,
  clients: clientsMapping,
} as const;
